import { TextSpan } from '../../Atoms/Text';
import { Switch } from '@mui/material';
import { AccountCustomizationData } from '../../../store/AccountCustomization';

export interface SettingsModelContentParams {
  columnsCustom: boolean;
  onEditColumns: () => void;
  widgetsCustom: boolean;
  onEditWidgets: () => void;
  handleCommentChange: () => void;
  handleSupplierContactsChange: () => void;
  handleNonRecurringInventoryChange: () => void;
  newCustomizations: AccountCustomizationData;
  orderRequestCustom: boolean;
  onEditOrderRequest: () => void;
  customFieldsCustom?: boolean;
  onEditCustomFields?: () => void;
}

export const SettingsModalContent = ({
  columnsCustom,
  onEditColumns,
  widgetsCustom,
  onEditWidgets,
  handleCommentChange,
  handleSupplierContactsChange,
  handleNonRecurringInventoryChange,
  newCustomizations,
  orderRequestCustom,
  onEditOrderRequest,
  customFieldsCustom,
  onEditCustomFields
}: SettingsModelContentParams) => {
  return (
    <div>
      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Table Columns:
          </TextSpan>
        </div>
        <div className="flex-none">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="sm">
            {columnsCustom ? 'Custom' : 'Default'}
          </TextSpan>
        </div>
        <div className="flex-none">
          <button
            data-testid="edit-columns"
            data-cy="edit-columns-btn"
            className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
            onClick={onEditColumns}>
            Edit
          </button>
        </div>
      </div>
      <div className="border-b-2 pb-3.5 mb-3.5">
        <TextSpan
          color="grey5"
          dataTestId="table-columns-title"
          size="xs">
          Set the default order and visibility of columns for users of this Account.
        </TextSpan>
      </div>

      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Widgets:
          </TextSpan>
        </div>
        <div className="flex-none">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="sm">
            {widgetsCustom ? 'Custom' : 'Default'}
          </TextSpan>
        </div>
        <div className="flex-none">
          <button
            className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
            onClick={onEditWidgets}>
            Edit
          </button>
        </div>
      </div>
      <div className="border-b-2 pb-3.5 mb-2">
        <TextSpan
          color="grey5"
          dataTestId="table-columns-title"
          size="xs">
          Set the default order and visibility of widgets for users of this Account.
        </TextSpan>
      </div>

      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Comments:
          </TextSpan>
        </div>
        <div className="flex-none">
          <div>
            <Switch
              sx={{
                '.Mui-checked': {
                  color: '#3B6FE8 !important'
                },
                '.MuiSwitch-track': {
                  backgroundColor: '#878EBE !important'
                }
              }}
              size="small"
              inputProps={{ 'aria-label': 'Comments' }}
              checked={newCustomizations.content.comments !== false}
              onChange={handleCommentChange}
            />
          </div>
        </div>
      </div>
      <div className="border-b-2 pb-3.5 mb-2">
        <TextSpan
          color="grey5"
          dataTestId="table-columns-title"
          size="xs">
          Enable comments on Orders, Inventory items and the services within Projects for users of this Account.
        </TextSpan>
      </div>

      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Supplier Contacts:
          </TextSpan>
        </div>
        <div className="flex-none">
          <div>
            <Switch
              sx={{
                '.Mui-checked': {
                  color: '#3B6FE8 !important'
                },
                '.MuiSwitch-track': {
                  backgroundColor: '#878EBE !important'
                }
              }}
              size="small"
              inputProps={{ 'aria-label': 'Supplier Contacts' }}
              checked={newCustomizations.content.supplier_contacts || false}
              onChange={handleSupplierContactsChange}
            />
          </div>
        </div>
      </div>
      <div className="border-b-2 pb-3.5 mb-2">
        <TextSpan
          color="grey5"
          dataTestId="table-columns-title"
          size="xs">
          Enable supplier contacts on Inventory items for users of this Account.
        </TextSpan>
      </div>

      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Display Non-Recurring Inventory:
          </TextSpan>
        </div>
        <div className="flex-none">
          <div>
            <Switch
              sx={{
                '.Mui-checked': {
                  color: '#3B6FE8 !important'
                },
                '.MuiSwitch-track': {
                  backgroundColor: '#878EBE !important'
                }
              }}
              size="small"
              inputProps={{ 'aria-label': 'Display Non-Recurring Inventory' }}
              checked={newCustomizations.content.non_recurring_inventory !== false}
              onChange={handleNonRecurringInventoryChange}
            />
          </div>
        </div>
      </div>
      <div className="border-b-2 pb-3.5 mb-2">
        <TextSpan
          color="grey5"
          dataTestId="table-columns-title"
          size="xs">
          Disable the display of inventory records of non-recurring/one-time services or fees on the inventory page.
          Default state is on.
        </TextSpan>
      </div>

      <div className="flex mt-2">
        <div className="grow">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="md">
            Order Request Settings:
          </TextSpan>
        </div>
        <div className="flex-none">
          <TextSpan
            className="flex items-center"
            color="grey10"
            size="sm">
            {orderRequestCustom ? 'Custom' : 'Default'}
          </TextSpan>
        </div>
        <div className="flex-none">
          <button
            className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
            onClick={onEditOrderRequest}>
            Edit
          </button>
        </div>
      </div>
      <TextSpan
        color="grey5"
        dataTestId="table-columns-title"
        size="xs">
        Configure the specific products to be displayed and whether the Account sees advanced or standard fields on the
        order request form.
      </TextSpan>

      {onEditCustomFields !== undefined && (
        <>
          <div className="flex mt-2 border-t-2 pt-3.5">
            <div className="grow">
              <TextSpan
                className="flex items-center"
                color="grey10"
                size="md">
                Custom Fields:
              </TextSpan>
            </div>
            <div className="flex-none">
              <TextSpan
                className="flex items-center"
                color="grey10"
                size="sm">
                {customFieldsCustom ? 'Custom' : 'Default'}
              </TextSpan>
            </div>
            <div className="flex-none">
              <button
                data-testid="edit-custom-fields"
                className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
                onClick={onEditCustomFields}>
                Edit
              </button>
            </div>
          </div>
          <TextSpan
            color="grey5"
            size="xs">
            Configure custom fields and their column labels for users of this Account.
          </TextSpan>
        </>
      )}
    </div>
  );
};
